// src/components/auth/hooks/useGoogleAuth.js
import { useEffect, useState, useRef } from 'react';
import { CONFIG } from '../constants';
import { logDebug, loadScript } from '../utils';
import { verifyGoogleLogin } from '../../../api/user';

export const useGoogleAuth = ({
  setIsLoading,
  setErrorMessage,
  onLoginSuccess,
  shouldLoad = true
}) => {
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [browserInfo, setBrowserInfo] = useState({
    isChrome: false,
    isFirefox: false,
    isSafari: false
  });
  const [apiError, setApiError] = useState(null);
  
  const scriptLoadingRef = useRef(false);
  const scriptLoadedRef = useRef(false);
  
  // 检测浏览器类型
  useEffect(() => {
    const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg|Edge/.test(navigator.userAgent);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    setBrowserInfo({ isChrome, isFirefox, isSafari });
    logDebug('浏览器检测', { isChrome, isFirefox, isSafari });
  }, []);

  // 加载 Google Sign-In SDK
  useEffect(() => {
    if (!shouldLoad || scriptLoadingRef.current || scriptLoadedRef.current) return;
    
    logDebug('开始加载 Google API');
    scriptLoadingRef.current = true;
    
    const handleScriptLoad = () => {
      logDebug('Google API 加载成功');
      setIsApiLoaded(true);
      setApiError(null);
      scriptLoadingRef.current = false;
      scriptLoadedRef.current = true;
    };
    
    const handleScriptError = () => {
      logDebug('Google API 加载失败');
      const loadError = '无法加载 Google 登录服务，请检查网络连接';
      setApiError(loadError);
      setIsApiLoaded(false);
      scriptLoadingRef.current = false;
      scriptLoadedRef.current = false;
    };

    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    
    if (existingScript) {
      if (window.google && window.google.accounts) {
        handleScriptLoad();
      } else {
        existingScript.addEventListener('load', handleScriptLoad);
        existingScript.addEventListener('error', handleScriptError);
      }
    } else {
      loadScript(
        "https://accounts.google.com/gsi/client",
        handleScriptLoad,
        handleScriptError
      );
    }
  }, [shouldLoad]);

  // 处理 Google 登录
  const handleGoogleLogin = async () => {
    if (!isApiLoaded || !window.google) {
      const error = 'Google 服务未就绪，请稍后重试';
      setApiError(error);
      setErrorMessage && setErrorMessage(error);
      setIsLoading && setIsLoading(false);
      return false;
    }
    
    try {
      logDebug('开始 Google 登录', browserInfo);
      setIsLoading && setIsLoading(true);
      
      if (browserInfo.isChrome) {
        // Chrome 使用 One Tap 登录
        logDebug('使用 One Tap 登录');
        
        window.google.accounts.id.initialize({
          client_id: CONFIG.GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: true,
          cancel_on_tap_outside: true
        });
        
        const result = await new Promise((resolve) => {
          window.google.accounts.id.prompt((notification) => {
            logDebug('One Tap 状态', notification);
            
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              logDebug('One Tap 无法显示，切换到重定向模式');
              handleRedirectGoogleLogin();
              resolve(false);
            } else if (notification.isDismissedMoment()) {
              logDebug('用户关闭了 One Tap');
              setIsLoading && setIsLoading(false);
              resolve(false);
            } else {
              resolve(true);
            }
          });
        });
        
        return result;
      } else {
        // 其他浏览器使用重定向
        logDebug('非 Chrome 浏览器，使用重定向登录');
        return handleRedirectGoogleLogin();
      }
    } catch (error) {
      logDebug('登录过程出错', error);
      setIsLoading && setIsLoading(false);
      setErrorMessage && setErrorMessage('登录失败，请重试');
      return false;
    }
  };

  // OAuth 2.0 重定向登录
  const handleRedirectGoogleLogin = () => {
    try {
      logDebug('使用重定向登录');
      
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      logDebug('重定向 URI', redirectUri);
      
      // 保存当前页面路径，登录后返回
      localStorage.setItem('googleAuthRedirectUrl', window.location.pathname);
      
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: CONFIG.GOOGLE_CLIENT_ID,
        scope: 'email profile',
        ux_mode: 'redirect',
        redirect_uri: redirectUri,
        state: btoa(JSON.stringify({ redirectTo: window.location.pathname }))
      });
      
      client.requestCode();
      return true;
    } catch (error) {
      logDebug('重定向失败', error);
      setErrorMessage && setErrorMessage('无法启动登录流程: ' + error.message);
      setIsLoading && setIsLoading(false);
      return false;
    }
  };

  // 处理 One Tap 响应（JWT Token）
  const handleGoogleCredentialResponse = async (response) => {
    logDebug('收到 Google 凭据');
    
    if (!response || !response.credential) {
      logDebug('无效的凭据响应');
      setErrorMessage && setErrorMessage('登录失败');
      setIsLoading && setIsLoading(false);
      return;
    }
    
    try {
      const credential = response.credential;
      
      logDebug('开始验证凭据');
      const result = await verifyGoogleLogin({ credential });
      
      if (!result.success) {
        throw new Error(result.message || '验证失败');
      }
      
      logDebug('验证成功');
      
      if (onLoginSuccess) {
        await onLoginSuccess(result.user || result.data);
      }
    } catch (error) {
      logDebug('凭据验证失败', error);
      setErrorMessage && setErrorMessage('登录失败: ' + (error.message || '未知错误'));
    } finally {
      setIsLoading && setIsLoading(false);
    }
  };

  // 处理授权码回调
  const handleAuthCallback = async (code) => {
    if (!code) {
      logDebug('未收到授权码');
      setErrorMessage && setErrorMessage('登录失败');
      return false;
    }
    
    try {
      setIsLoading && setIsLoading(true);
      logDebug('开始验证授权码');
      
      const result = await verifyGoogleLogin({ 
        authCode: code,
        redirectUri: `${window.location.origin}/auth/google/callback`
      });
      
      if (!result.success) {
        throw new Error(result.message || '验证失败');
      }
      
      logDebug('授权码验证成功');
      
      if (onLoginSuccess) {
        await onLoginSuccess(result.user || result.data);
      }
      
      return true;
    } catch (error) {
      logDebug('授权码验证失败', error);
      setErrorMessage && setErrorMessage('登录失败: ' + (error.message || '未知错误'));
      setIsLoading && setIsLoading(false);
      return false;
    }
  };

  // 重新加载 Google API
  const reloadGoogleApi = () => {
    logDebug('重新加载 Google API');
    setIsApiLoaded(false);
    setApiError(null);
    
    const existingScripts = document.querySelectorAll('script[src="https://accounts.google.com/gsi/client"]');
    existingScripts.forEach(script => script.remove());
    
    scriptLoadingRef.current = false;
    scriptLoadedRef.current = false;
    
    // 触发重新加载
    setTimeout(() => {
      if (shouldLoad) {
        loadScript(
          "https://accounts.google.com/gsi/client",
          () => {
            setIsApiLoaded(true);
            setApiError(null);
          },
          () => {
            setApiError('无法加载 Google 登录服务');
            setIsApiLoaded(false);
          }
        );
      }
    }, 100);
  };

  return {
    isApiLoaded,
    browserInfo,
    handleGoogleLogin,
    handleAuthCallback,
    apiError,
    reloadGoogleApi
  };
};